const NEW_SIM_PRODUCT = 15488887718202;
const SERVICE_PRODUCT = 15488988152122;
const ANNUAL_PLAN = 694433415482;
const PAIRS = new Map([[67869474292026,67869635477818],[67869474324794,67869635510586],[67869474357562,67869635543354],[67869474390330,67869635576122]]);
const root = () => window.Shopify?.routes?.root || '/';

export function validateSimCart(cart) {
  const cards = cart.items.filter(item => item.product_id === NEW_SIM_PRODUCT);
  const services = cart.items.filter(item => item.product_id === SERVICE_PRODUCT);
  return cards.every(card => {
    const children = services.filter(service => service.parent_relationship?.parent_key === card.key);
    return !card.selling_plan_allocation && card.requires_shipping === true && children.length === 1 &&
      children[0].variant_id === PAIRS.get(card.variant_id) && children[0].quantity === card.quantity &&
      children[0].selling_plan_allocation?.selling_plan?.id === ANNUAL_PLAN &&
      children[0].requires_shipping === false && children[0].final_line_price === 0;
  }) && services.every(service => cards.some(card => card.key === service.parent_relationship?.parent_key));
}

class NewSimPurchase extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.component = this.closest('product-form-component');
    this.messages = JSON.parse(this.querySelector('[data-sim-messages]').textContent);
    this.onSelection = event => {
      if (event.promise) Promise.resolve(event.promise).then(() => this.sync()).catch(() => {});
      else this.sync();
    };
    document.addEventListener('product:select',this.onSelection);
    this.form?.addEventListener('change',this.onSelection);
    this.onPageShow = () => { this.restoreButtons(); this.sync(); };
    window.addEventListener('pageshow', this.onPageShow);
    this.sync();
  }
  disconnectedCallback() {
    document.removeEventListener('product:select',this.onSelection);
    this.form?.removeEventListener('change',this.onSelection);
    window.removeEventListener('pageshow', this.onPageShow);
  }
  restoreButtons() {
    this.busy = false;
    this.buttons?.forEach(({ button, disabled }) => {
      button.disabled = disabled;
      button.removeAttribute('aria-busy');
    });
    this.buttons = undefined;
  }
  sync() {
    const id = this.form?.querySelector('[name="id"]')?.value;
    if(this.currentVariant && this.currentVariant !== id) this.querySelector('[data-sim-consent]').checked = false;
    this.currentVariant = id;
    this.querySelectorAll('[data-sim-variant]').forEach(row => {row.hidden = row.dataset.simVariant !== id;});
  }
  error(message) {
    const output = this.querySelector('[data-sim-error]');
    output.textContent = message; output.hidden = false; this.focus();
  }
  async add(form) {
    if(this.busy) return;
    this.sync();
    const data = new FormData(form);
    const id = Number(data.get('id'));
    const quantity = Number(data.get('quantity') || 1);
    const row = Array.from(this.querySelectorAll('[data-sim-variant]')).find(item => Number(item.dataset.simVariant) === id);
    if(this.dataset.launchReady !== 'true' || !row || row.dataset.cardAvailable !== 'true' || row.dataset.serviceReady !== 'true' || !Number.isInteger(quantity) || quantity < 1) {
      this.error(this.messages.unavailable); return;
    }
    if(!this.querySelector('[data-sim-consent]').checked) { this.error(this.messages.consent); return; }
    if(!form.reportValidity()) return;
    const pair = crypto.randomUUID();
    const properties = {'_t2m_sim_pair':pair};
    for(const [key,value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match) continue;
      if (typeof value !== 'string') { this.error(this.messages.failed); return; }
      properties[match[1]] = value;
    }
    const authorization = `${row.dataset.planName}: first service year included; ${row.dataset.renewalPrice} per SIM annually starting one year after checkout, until canceled. Service only.`;
    const items = [
      {id,quantity,properties:{...properties,'Plan renewal':authorization}},
      {id:PAIRS.get(id),quantity,selling_plan:ANNUAL_PLAN,parent_id:id,properties:{'_t2m_sim_pair':pair,'Renewal authorization':authorization}}
    ];
    this.busy = true;
    const buttons = Array.from(form.querySelectorAll('button[type="submit"]')).map(button=>({button,disabled:button.disabled}));
    this.buttons = buttons;
    buttons.forEach(({button})=>{button.disabled=true;button.setAttribute('aria-busy','true');});
    try {
      const response = await fetch(root()+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items})});
      const result = await response.json();
      if(!response.ok || result.status) throw new Error(this.messages.failed);
      const cartResponse = await fetch(root()+'cart.js',{cache:'no-store'});
      if(!cartResponse.ok) throw new Error(this.messages.failed);
      const cart = await cartResponse.json();
      const added = cart.items.filter(item=>item.properties?._t2m_sim_pair === pair);
      if(added.length !== 2 || !validateSimCart({items:added})) throw new Error(this.messages.failed);
      window.location.assign(root()+'cart');
    } catch(error) {
      // Remove only this attempt's uniquely marked lines, including partial adds.
      try {
        const cart = await (await fetch(root()+'cart.js',{cache:'no-store'})).json();
        const updates = Object.fromEntries(cart.items.filter(item=>item.properties?._t2m_sim_pair === pair).map(item=>[item.key,0]));
        if(Object.keys(updates).length) await fetch(root()+'cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({updates})});
      } catch { /* The cart guard will prevent an incomplete pair checking out. */ }
      this.error(this.messages.failed);
    } finally {
      this.restoreButtons();
    }
  }
}
if(!customElements.get('t2m-new-sim-purchase')) customElements.define('t2m-new-sim-purchase',NewSimPurchase);

// This storefront guard supplements the required server-side checkout rules.
class NewSimCartGuard extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const signal = this.abort.signal;
    const block = async event => {
      const target = event.target instanceof Element ? event.target : null;
      const control = event.type === 'submit'
        ? (event.submitter?.matches('[name="checkout"]') ? event.submitter : null)
        : target?.closest('[name="checkout"],a[href*="/checkout"]');
      if (!control || this.replaying) return;
      event.preventDefault(); event.stopImmediatePropagation();
      try {
        const response = await fetch(root()+'cart.js',{cache:'no-store'});
        if(!response.ok || !validateSimCart(await response.json())) throw new Error('Incomplete SIM subscription');
        this.replaying = true;
        try {
          if (event.type === 'submit') control.form.requestSubmit(control);
          else control.click();
        } finally {
          this.replaying = false;
        }
      } catch {
        const warning = this.querySelector('[data-sim-cart-error]');
        warning.hidden = false; warning.focus();
      }
    };
    document.addEventListener('click',block,{capture:true,signal});
    document.addEventListener('submit',block,{capture:true,signal});
  }
  disconnectedCallback() { this.abort?.abort(); }
}
if(!customElements.get('t2m-new-sim-cart-guard')) customElements.define('t2m-new-sim-cart-guard',NewSimCartGuard);
